import React, { useState, useEffect } from "react";
import { GoTrash } from "react-icons/go";
import { IoMdAdd } from "react-icons/io";
import { FiEdit2, FiSearch, FiTag } from "react-icons/fi";
import ConfirmationModal from "../../../components/ui/ConfirmationModal";
import PageHeader from "../../../components/ui/PageHeader";
import LoadingSpinner from "../../../components/ui/LoadingSpinner";
import Button from "../../../components/ui/Button";
import { strings } from "../../../strings/strings";
import BrandModal from "./BrandModal";
import { apiCall } from "../../../api/fetchClient";
import { 
  GET_TRUSTED_BY_LIST_URL, 
  DELETE_TRUSTED_BY_URL
} from "../../../api/url";

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, brand: null });

  const fetchBrands = async () => {
    setIsLoading(true);
    try {
      const response = await apiCall(GET_TRUSTED_BY_LIST_URL, "GET", null, false, false);
      if (response?.results?.data) {
        setBrands(response.results.data);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const filteredBrands = Array.isArray(brands) 
    ? brands.filter(b => b.name?.toLowerCase().includes(searchQuery.toLowerCase()))
    : [];

  const handleAdd = () => {
    setSelectedBrand(null);
    setIsModalOpen(true);
  };

  const handleEdit = (brand) => {
    setSelectedBrand(brand);
    setIsModalOpen(true);
  };

  const handleDelete = (brand) => {
    setConfirmModal({ isOpen: true, brand });
  };

  const confirmDelete = async () => {
    try {
      await apiCall(`${DELETE_TRUSTED_BY_URL}${confirmModal.brand._id || confirmModal.brand.id}`, "DELETE", null, false, true);
      fetchBrands();
    } catch (error) {
    } finally {
      setConfirmModal({ isOpen: false, brand: null });
    }
  };


  const handleSave = () => {
    fetchBrands();
    setIsModalOpen(false);
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        title={strings.BRANDS_MANAGEMENT}
        description={strings.BRANDS_DESCRIPTION}
        icon={FiTag}
      >
        <Button onClick={handleAdd}>
          <IoMdAdd className="w-4 h-4 mr-2" />
          {strings.ADD_BRAND}
        </Button>
      </PageHeader>

      <div className="mb-6 bg-white dark:bg-slate-900 rounded-lg p-4 shadow-sm border border-gray-100 dark:border-slate-800">
        <div className="relative w-full max-w-md">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={strings.SEARCH_BRANDS}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-800 dark:text-white outline-none"
          />
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner message="Loading brands..." minHeight="h-64" />
      ) : filteredBrands.length > 0 ? (
        <div className="grid gap-6 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
          {filteredBrands.map((brand) => (
            <div key={brand._id || brand.id} className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-800 p-6 flex flex-col hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-4">
                <div className="h-16 w-16 bg-gray-100 dark:bg-slate-800 rounded-lg flex items-center justify-center overflow-hidden">
                  {brand.logo ? (
                    <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-2xl font-bold text-gray-400">{brand.name?.charAt(0)}</span>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  <button onClick={() => handleEdit(brand)} className="p-2 text-gray-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors">
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(brand)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-colors">
                    <GoTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">{brand.name}</h3>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800">
          <p className="text-gray-500 dark:text-gray-400">{strings.NO_BRANDS_YET}</p>
          <Button onClick={handleAdd} className="mt-4">
            {strings.ADD_BRAND}
          </Button>
        </div>
      )}

      <BrandModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        brand={selectedBrand}
      />

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, brand: null })}
        onConfirm={confirmDelete}
        title={strings.DELETE_BRAND_TITLE}
        message={confirmModal.brand ? strings.DELETE_BRAND_MESSAGE(confirmModal.brand.name) : ""}
        confirmText={strings.DELETE}
        variant="danger"
        icon={GoTrash}
      />
    </div>
  );
};

export default BrandsPage;
